import { Tooltip } from 'components/Tooltip/tooltip'
import { MdClose } from 'react-icons/md'
import styled from 'styled-components/macro'
import {
  FilterCriteria,
  FilterStore,
  RangeFilter,
  ValueFilter,
  ZipCodeFilterState,
} from '../FilterStore'
import { useMemo } from 'react'
import { FakeLink } from 'components/FbUI/FbLink/styles'

export default function FilterChips({
  clearAllButton = false,
  showActive = false,
  store,
}: {
  clearAllButton?: boolean
  showActive?: boolean
  store: FilterStore
}) {
  const {
    pendingCriteria,
    criteria,
    pendingValueFilters,
    valueFilters,
    filterTitles,
    rangeFilters,
    pendingRangeFilters,
    clearPendingValueFilter,
    clearValueFilter,
    clearPendingRangeFilter,
    clearRangeFilter,
    clearAllFilters,
    clearAllPendingFilters,
    pendingZipCodeFilter,
    zipCodeFilter,
    clearPendingZipCodeFilter,
    clearZipCodeFilter,
    search,
    pendingSearch,
    clearSearch,
    clearPendingSearch,
  } = store()

  let _valueFilters = pendingValueFilters
  let _rangeFilters = pendingRangeFilters
  let _zipCodeFilter = pendingZipCodeFilter
  let _clearValueFilter = clearPendingValueFilter
  let _clearRangeFilter = clearPendingRangeFilter
  let _search = pendingSearch
  let _clearSearch = clearPendingSearch
  let _criteria = pendingCriteria
  let _clearAll = clearAllPendingFilters
  let _clearZipCodeFilter = clearPendingZipCodeFilter

  if (showActive) {
    _valueFilters = valueFilters
    _rangeFilters = rangeFilters
    _zipCodeFilter = zipCodeFilter
    _clearValueFilter = clearValueFilter
    _clearRangeFilter = clearRangeFilter
    _search = search
    _clearSearch = clearSearch
    _criteria = criteria
    _clearAll = clearAllFilters
    _clearZipCodeFilter = clearZipCodeFilter
  }

  const hasFilters = useMemo(
    () =>
      Object.entries(_valueFilters).length > 0 ||
      Object.entries(_rangeFilters).length > 0 ||
      Object.values(_search).filter((s) => !!s.value).length > 0 ||
      _zipCodeFilter[0],
    [_valueFilters, _rangeFilters, _search, _zipCodeFilter]
  )

  if (!hasFilters) return null

  return (
    <div className="d-flex flex-wrap align-items-center gap-2">
      {_zipCodeFilter[0] && (
        <FilterZipRange
          label="Zip Code"
          active={_zipCodeFilter}
          onClose={() => {
            _clearZipCodeFilter()
          }}
          isExclusive={_criteria === 'neither'}
        />
      )}

      {Object.entries(_rangeFilters).map(([key, value]) => (
        <FilterChipRange
          key={key}
          filterCriteria={_criteria}
          value={value}
          title={filterTitles[key]}
          onClose={() => _clearRangeFilter(key)}
        />
      ))}

      {Object.entries(_valueFilters).map(([key, value]) => (
        <FilterChipValue
          key={key}
          filterCriteria={_criteria}
          value={value}
          title={filterTitles[key]}
          onClose={() => _clearValueFilter(key)}
        />
      ))}

      {Object.keys(_search).map((key) => (
        <SearchChipValue
          key={key}
          search={_search[key].value}
          searchLabel={_search[key].label}
          onClose={() => {
            _clearSearch(key)
          }}
        />
      ))}

      {clearAllButton && hasFilters && (
        <FakeLink onClick={_clearAll}>Clear all</FakeLink>
      )}
    </div>
  )
}

type FilterChipRangeProps = {
  title?: string
  value: RangeFilter
  onClose?: () => void
  filterCriteria: FilterCriteria
}

export function FilterChipRange({
  title,
  value,
  onClose,
  filterCriteria,
}: FilterChipRangeProps) {
  const { max, min } = value
  const isExclusive = filterCriteria === 'neither'

  const formatStrAsLocaleDate = (str: string) => {
    const date = new Date(str + 'T00:00:00')
    return date.toLocaleDateString()
  }

  const formatValue = (value: number | string) => {
    const isString = typeof value === 'string'
    const has3hyphens = value.toString().split('-').length === 3
    if (isString && has3hyphens) {
      return formatStrAsLocaleDate(value)
    }
    return value.toString()
  }

  const getFormattedValue = () => {
    if (min == null && max != null) return `≤${formatValue(max)}`
    if (max == null && min != null) return `≥${formatValue(min)}`

    return `${formatValue(min!)} - ${formatValue(max!)}`
  }

  return (
    <FilterChipContainer isExclusive={isExclusive}>
      <span>{`${title}: ${getFormattedValue()}`}</span>
      <FilterChipCloseButton
        onClick={onClose}
        style={{
          color: isExclusive ? '#E44C4C' : '#087443',
        }}
      />
    </FilterChipContainer>
  )
}

type FilterChipValue = {
  title?: string
  value: ValueFilter[]
  onClose?: () => void
  filterCriteria: FilterCriteria
}

export function FilterChipValue({
  onClose,
  value,
  title,
  filterCriteria,
}: FilterChipValue) {
  const isExclusive = filterCriteria === 'neither'
  const getFormattedValue = () => {
    if (value.length === 1) return value[0]?.label
    return `${value.length} selected`
  }

  const selectedString = useMemo(() => {
    const selected = value.map((v) => v.label)
    if (selected.length < 1) return ''
    return selected.join(', ')
  }, [value])

  return (
    <Tooltip delay={0} content={selectedString} placement="top">
      <FilterChipContainer isExclusive={isExclusive}>
        <span>{`${title}: ${getFormattedValue()}`}</span>
        <FilterChipCloseButton
          style={{
            color: isExclusive ? '#E44C4C' : '#087443',
          }}
          onClick={onClose}
        />
      </FilterChipContainer>
    </Tooltip>
  )
}

export type FilterZipRangeProps = {
  label: string
  active: ZipCodeFilterState
  onClose?: () => void
  isExclusive: boolean
}

export function FilterZipRange({
  label,
  active,
  onClose,
  isExclusive,
}: FilterZipRangeProps) {
  const getFormattedValue = () => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    const zipcode = active?.[0]!
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    const range = active?.[1]!
    return `${zipcode} (${range} mile${range > 1 ? 's' : ''})`
  }

  return (
    <FilterChipContainer isExclusive={isExclusive}>
      <span>{`${label}: ${getFormattedValue()}`}</span>
      <FilterChipCloseButton onClick={onClose} isExclusive={isExclusive} />
    </FilterChipContainer>
  )
}

export function SearchChipValue({
  search,
  onClose,
  searchLabel = 'Search',
}: {
  search: string
  onClose?: () => void
  searchLabel?: string
}) {
  if (!search) return null
  return (
    <FilterChipContainer>
      <span>{`${searchLabel}: ${search}`}</span>
      {onClose && <FilterChipCloseButton onClick={onClose} />}
    </FilterChipContainer>
  )
}

export const FilterChipContainer = styled.span<{
  isExclusive?: boolean
}>`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 20px;
  color: #087443;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  border-radius: 10px;

  border: 1px solid rgba(71, 84, 103, 0);
  background: rgba(8, 116, 67, 0.1);

  ${({ isExclusive }) =>
    isExclusive &&
    `
    background-color: rgba(228, 76, 76, 0.10);
    color: #E44C4C;
    `}
`

export const FilterChipCloseButton = styled(MdClose)<{
  isExclusive?: boolean
}>`
  margin-left: 8px;
  color: ${(props) => (props.isExclusive ? '#E44C4C' : '#087443')};
  font-size: 18px;
  cursor: pointer;
  line-height: 18px;
`
